import formatters from './formatters';
import { isValidCPF, isValidCNPJ } from '@brazilian-utils/brazilian-utils';
import { dddNumbers } from '../constants/entity';
import { validate as uuidValidate } from 'uuid';
import { PixKeyType } from '../constants/banking';

type Joinable = string | null | undefined | false;

export const join = (args: Joinable[], separator = ' '): string =>
    args.filter(x => x).join(separator);

export const classes = (...args: Joinable[]): string => join(args);

export const includesNormalized = (
    source: string | string[],
    substr: string
): boolean => {
    return (Array.isArray(source) ? source : [source]).some(str =>
        formatters.normalize(str).includes(formatters.normalize(substr))
    );
};

export const removeSpecialCharacters = (string: string): string => {
    return string.replace(/[^a-zA-Z0-9\s]/g, '');
};

export const EMAIL_REGEX = new RegExp(
    "^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:[.][a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@" +
        '(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?[.])+[a-zA-Z0-9]' +
        '(?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$'
);

export const UUID_REGEX = new RegExp(
    '^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$'
);

export const PHONE_CELLPHONE_REGEX = RegExp(
    `^(${dddNumbers.join('|')})[0-9]{8,9}$`
);
export const CELLPHONE_REGEX = RegExp(`^(${dddNumbers.join('|')})[0-9]{9}$`);

export const PIX_CELLPHONE_REGEX = RegExp(
    `^\\+55(${dddNumbers.join('|')})[0-9]{9}$`
);

export const FIRST_NUMERIC_URL_PARAMETER_REGEX = '^/(\\d+)(/.*)?$';

export const ADDRESS_REGEX = RegExp(/^[\w\s+\-._,A-zÀ-ú]+$/);

// eslint-disable-next-line complexity
export function inferPixKeyType(key: string): PixKeyType | undefined {
    if (key.match(PIX_CELLPHONE_REGEX)) {
        return 'phone';
    } else if (isValidCPF(key)) {
        return 'cpf';
    } else if (isValidCNPJ(key)) {
        return 'cnpj';
    } else if (key.match(EMAIL_REGEX)) {
        return 'email';
    } else if (uuidValidate(key)) {
        return 'uuid';
    }
    return undefined;
}

export function onlyNumbers(s: string) {
    return s.replace(/\D/g, '');
}

export function removeDuplicatedSpaces(s: string) {
    return s.replace(/\s+/g, ' ');
}

/**
 * Snake case formatting that considers only capital letters as the start of
 * a word. This formatting differs from lodash's due to the latter
 * considering numbers as valid start of words also. A concrete example of
 * this is `fileS3Path`. This formatter outputs `file_s3_path` while lodash's
 * output `file_s_3_path`.
 */
export function numberSafeCamelToSnakeCase(s: string) {
    return s.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

export const limitSize = (text: string, maxLength: number) => {
    const trimmed = text.trim();
    return trimmed.length > maxLength
        ? `${trimmed.slice(0, maxLength - 3)}...`
        : trimmed;
};
